import * as THREE from 'three';
import { LineBasicMaterial } from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { FikoThreeJsComponent } from '../fiko-three-js.component';
import { FikoGrup } from './FikoGrup';
import { FikoSahne } from './FikoSahne';
import { hesaplaAgirlik, hesaplaHacim, hesaplaAlan } from './FikoYT-Fonksiyonlar';
import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FikoKonumlar } from './FikoKonumlar/FikoKonumlar';
import { Fiko3DIsaretleyiciler } from './Fiko3DIsaretleyiciler';
import { jsondan } from '../FikoThreeType';
import { CncEstimator } from './FikoThreeCam/FikoIslemeTahmincisi';
import { FikoKutuk } from './FikoKutuk';
import { FikoBrep, MeshAnalysisResult, BrepBody } from './FikoBrep/FikoBrep';
import { FikoGPUBrepExtractor, GrupBrepResult } from './FikoBrep/FikoGPUBrepExtractor';
import { getCanvasResolution } from './FikoCanvasResolution';
declare var flate: any;

/**
 * Binary buffer collector — aktifken geometryToCompact() base64 yerine
 * ham binary referanslar (_bin index) üretir. ~40% daha küçük dosya boyutu.
 */
let _activeBinaryCollector: Uint8Array[] | null = null;

/**
 * Byte Shuffle (HDF5/Blosc tekniği) — %100 LOSSLESS
 * Float32: [b0b1b2b3, b0b1b2b3, ...] → [tüm b0'lar, tüm b1'ler, tüm b2'ler, tüm b3'ler]
 * Benzer byte'lar yan yana gelir → gzip 2-4x daha iyi sıkıştırır.
 */
function byteShuffle(input: Uint8Array, elementSize: number): Uint8Array {
    if (elementSize <= 1) return input;
    const len = input.length;
    const count = (len / elementSize) | 0;
    const out = new Uint8Array(len);
    for (let i = 0; i < count; i++) {
        for (let b = 0; b < elementSize; b++) {
            out[b * count + i] = input[i * elementSize + b];
        }
    }
    // Kalan byte'lar (hizalama artığı)
    const remainder = len - count * elementSize;
    if (remainder > 0) {
        out.set(input.subarray(count * elementSize), count * elementSize);
    }
    return out;
}

function byteUnshuffle(input: Uint8Array, elementSize: number): Uint8Array {
    if (elementSize <= 1) return input;
    const len = input.length;
    const count = (len / elementSize) | 0;
    const out = new Uint8Array(len);
    for (let i = 0; i < count; i++) {
        for (let b = 0; b < elementSize; b++) {
            out[i * elementSize + b] = input[b * count + i];
        }
    }
    const remainder = len - count * elementSize;
    if (remainder > 0) {
        out.set(input.subarray(count * elementSize), count * elementSize);
    }
    return out;
}

/**
 * XYZ Deinterleave — Float32 pozisyon verisi için %100 LOSSLESS
 * [x0,y0,z0, x1,y1,z1, ...] → [x0,x1,..., y0,y1,..., z0,z1,...]
 * Aynı eksendeki değerler yan yana → byte shuffle ile birlikte çok etkili
 */
function deinterleaveXYZ(f32: Float32Array): Float32Array {
    const vtxCount = (f32.length / 3) | 0;
    const out = new Float32Array(f32.length);
    for (let v = 0; v < vtxCount; v++) {
        out[v]                  = f32[v * 3];     // X kanalı
        out[vtxCount + v]       = f32[v * 3 + 1]; // Y kanalı
        out[vtxCount * 2 + v]   = f32[v * 3 + 2]; // Z kanalı
    }
    return out;
}

function reinterleaveXYZ(f32: Float32Array, vtxCount: number): Float32Array {
    const out = new Float32Array(vtxCount * 3);
    for (let v = 0; v < vtxCount; v++) {
        out[v * 3]     = f32[v];                // X
        out[v * 3 + 1] = f32[vtxCount + v];     // Y
        out[v * 3 + 2] = f32[vtxCount * 2 + v]; // Z
    }
    return out;
}

/**
 * PERFORMANCE OPTIMIZATION: Shared Material Cache
 * ================================================
 * Reduces material allocations when multiple FikoCizimYalin instances share
 * the same material properties (vertexColors, side, emissive).
 *
 * Cache key format: "vertexColors_side_emissive"
 * Example: "true_2_0" -> vertexColors=true, DoubleSide, emissive=0x0
 *
 * Clear cache after large imports: FikoCizimYalin.clearMaterialCache()
 */

/**
 * FikoCizimYalin için basılı tutma menüsü - ağırlık, hacim, alan bilgilerini gösterir
 */
@Component({
    selector: 'fikoyt-cizim-yalin-ek-bilesen',
    standalone: true,
    template: `
    <div style="background-color: transparent;color: white;max-width:100%;">
        <div style="display: flex;align-items: center;justify-content: center;">
        <div style="display: flex;flex-direction: column;flex:1;">
                    <!-- Ağırlık -->
                    <div>
                        <div style="color:yellow; font-size: 1.0em;text-align:center;">
                               Ağırlık :
                        </div>
                        <div style="font-weight: bold;text-align:center; font-size: 1.0em;">🔩{{formatAgirlik(element.agirlik)}}</div>
                    </div>

                    <!-- Density (Özkütle) -->
                    <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #444;">
                        <div style="color:#87CEEB; font-size: 0.9em;text-align:center;">
                               Özkütle (g/cm³) :
                        </div>
                        <div style="display: flex; gap: 5px; align-items: center; justify-content: center;">
                            <input
                                type="number"
                                [(ngModel)]="densityValue"
                                (input)="updateDensity()"
                                (change)="updateDensity()"
                                step="0.1"
                                min="0.1"
                                style="width: 80px; padding: 4px; background-color: #333; color: white; border: 1px solid #666; border-radius: 3px; text-align: center;">
                            <button
                                (click)="resetDensity()"
                                style="padding: 4px 8px; background-color: #666; color: white; border: 1px solid #888; border-radius: 3px; cursor: pointer; font-size: 0.85em;">
                                Sıfırla
                            </button>
                        </div>
                        <div style="color:#999; font-size: 0.8em; text-align:center; margin-top: 3px;">
                            (Al: 2710, Çelik: 7800, Bakır: 8900)
                        </div>
                    </div>

                    <!-- Kütük ölçüleri -->
                    <div *ngIf="getKutukOlculeri()" style="margin-top: 3px; padding-top: 3px; border-top: 1px solid #444;">
                        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 5px;">
                            <div style="flex: 1;"> Kütük Ölçüleri </div>
                            <button
                                (click)="toggleKutukGoruntu()"
                                style="padding: 3px 3px; background-color: #fff; color: white; border: 1px solid #777; border-radius: 2px; cursor: pointer; font-size: 0.75em; white-space: nowrap;">
                                {{ element.kutuk?.visible ? '🔍' : '👁️‍🗨️'}}
                            </button>
                        </div>
                        <div style="display: flex; gap: 5px; justify-content: space-between;">
                            <div style="text-align: center;">
                                <div style="color:#FFA500; font-size: 1.0em;">X<br>(Büyük)</div>
                                <div style="font-weight: bold;">{{getKutukOlculeri()?.x.toFixed(3)}} </div>
                            </div>
                            <div style="text-align: center;">
                                <div style="color:#FFD700; font-size: 1.0em;">Y<br>(Orta)</div>
                                <div style="font-weight: bold;">{{getKutukOlculeri()?.y.toFixed(3)}} </div>
                            </div>
                            <div style="text-align: center;">
                                <div style="color:#87CEEB; font-size: 1.0em;">Z<br>(Küçük)</div>
                                <div style="font-weight: bold;">{{getKutukOlculeri()?.z.toFixed(3)}} </div>
                            </div>
                        </div>

                        <!-- Yön Seçme Butonları -->
                        <div style="margin-top: 8px; display: flex; gap: 3px; flex-wrap: wrap; justify-content: center;">
                            <button
                                (click)="baslat3EksenModu()"
                                [disabled]="element.kutuk?.twoAxisMode === true"
                                style="padding: 4px 8px; background-color: #444; color: white; border: 1px solid #666; border-radius: 3px; cursor: pointer; font-size: 0.8em; flex: 1; min-width: 80px;"
                                [style.opacity]="element.kutuk?.twoAxisMode === true ? '0.5' : '1'">
                                🔄 Yön Seç
                            </button>
                            <button
                                (click)="resetKutuk()"
                                [disabled]="!element.kutuk?.roughStockResult"
                                style="padding: 4px 8px; background-color: #666; color: white; border: 1px solid #888; border-radius: 3px; cursor: pointer; font-size: 0.8em; flex: 1; min-width: 80px;"
                                [style.opacity]="!element.kutuk?.roughStockResult ? '0.5' : '1'">
                                ↻ Sıfırla
                            </button>
                        </div>
                    </div>
                </div>
        </div>
    </div>
    `,
    styleUrl: '../Fiko3DComponent/.component3D.scss',
    imports: [
        CommonModule,
        FormsModule,
    ]
})
export class FikoCizimYalinEkBilesen implements OnInit, OnDestroy {
    @Input() element: any;
    densityValue: number = 2710; // Default: Alüminyum (g/cm³ cinsinden)

    constructor(public CD: ChangeDetectorRef) { }

    ngOnInit() {
        // Element yüklendiğinde density değerini al (kg/cm³'den g/cm³'ye çevir)
        // 0.00271 kg/cm³ → 2710 g/cm³ (1000 ile çarp)
        if (this.element?.density !== undefined) {
            this.densityValue = this.element.density * 1000000; // kg/cm³ → g/cm³ (0.00271 * 1000000 = 2710)
        }
    }

    /**
     * Density değerini güncelle ve element'e uygula
     * Input değeri gram/cm³ cinsinden, element'e kg/cm³ olarak gönder
     */
    updateDensity(): void {
        if (this.element && !isNaN(this.densityValue) && this.densityValue > 0) {
            // g/cm³'ten kg/cm³'ye çevir (2710 g/cm³ = 2.71 kg/cm³)
            const densityKgCm3 = this.densityValue / 1000000;
            this.element.density = densityKgCm3;

            // Cache'i sıfırla - ağırlık hemen güncellensin
            if (this.element.invalidatePropertyCache) {
                this.element.invalidatePropertyCache();
            }
        }
        this.CD.detectChanges();
    }

    /**
     * Density değerini sıfırla (default Alüminyum değerine)
     */
    resetDensity(): void {
        this.densityValue = 2710; // Alüminyum: 2710 g/cm³
        if (this.element) {
            this.element.density = 2710 / 1000000; // kg/cm³'ye çevir (2.71)

            // Cache'i sıfırla
            if (this.element.invalidatePropertyCache) {
                this.element.invalidatePropertyCache();
            }
        }
        this.CD.detectChanges();
    }

    /**
     * Ağırlığı uygun birim ile gösterir (kg, gr, mg)
     * @param agirlikKg ağırlık değeri (kg cinsinden)
     * @returns Formatlanmış ağırlık string'i
     */
    formatAgirlik(agirlikKg: number): string {
        if (!agirlikKg || isNaN(agirlikKg)) {
            return '0 kg';
        }

        const agirlikGram = agirlikKg * 1000; // gr'a çevir
        const agirlikMilligram = agirlikGram * 1000; // mg'a çevir

        // En büyük birimi seç (1'den başlayarak)
        if (agirlikKg >= 1) {
            return `${agirlikKg.toFixed(3)} kg`;
        } else if (agirlikGram >= 1) {
            return `${agirlikGram.toFixed(3)} gr`;
        } else {
            return `${agirlikMilligram.toFixed(3)} mg`;
        }
    }

    /**
     * Kütük görünürlüğünü toggle et
     */
    toggleKutukGoruntu(): void {
        if (this.element?.kutuk) {
            this.element.kutuk.visible = !this.element.kutuk.visible;
            this.element?.kutuk?.sahne?.anaGosterim?.ciz();
            this.CD.detectChanges();
        }
    }

    /**
     * Kütük ölçülerini hesapla
     * X = En büyük boyut, Y = Orta boyut, Z = En küçük boyut
     */
    getKutukOlculeri(): { x: number, y: number, z: number } | null {
        if (!this.element || !this.element.kutuk || !this.element.kutuk.roughStockResult) {
            return null;
        }

        const sizes = this.element.kutuk.roughStockResult.sizes;
        const axes = this.element.kutuk.roughStockResult.axes;

        // Eksen boyutlarını al ve büyük->küçük sırala
        const boyutlar = [
            { eksen: 'X', deger: Math.abs(sizes.x), axis: axes[0] },
            { eksen: 'Y', deger: Math.abs(sizes.y), axis: axes[1] },
            { eksen: 'Z', deger: Math.abs(sizes.z), axis: axes[2] }
        ];

        // Büyükten küçüğe sırala
        boyutlar.sort((a, b) => b.deger - a.deger);

        return {
            x: boyutlar[0].deger, // En büyük
            y: boyutlar[1].deger, // Orta
            z: boyutlar[2].deger  // En küçük
        };
    }

    /**
     * Yön seçme modunu başlat (fare oku simgesiyle yüzey seçimi)
     */
    baslat3EksenModu(): void {
        if (!this.element?.kutuk) {
            return;
        }
        // ✅ Menüyü kapatmayı engelle
        this.element.kapatmayiEngelle = true;

        // Yüzey seçimini başlat
        this.element.kutuk.yuzeySecimiBaslat();



        this.CD.detectChanges();
    }

    /**
     * Kütüğü sıfırla - (0,0,1) default yönüne göre kütüğü güncelle
     */
    resetKutuk(): void {
        if (!this.element?.kutuk) {
            return;
        }

        // Seçilen yüzü temizle - (0,0,1) default yönü kullan
        this.element.kutuk.secilenFaceNormal = null;
        this.element.kutuk.roughStockOptions.alignToFace = null;

        // Kütüğü default yönle yeniden oluştur
        this.element.kutuk.roughStockOlusturCizimIcin();

        // Menüyü kapatmaya izin ver
        this.element.kapatmayiEngelle = false;

        this.CD.detectChanges();

        this.element?.kutuk?.sahne?.anaGosterim?.ciz();
    }

    /**
     * Yön seçimi aktif mi kontrol et
     */
    isYonSecimiAktif(): boolean {
        // FikoSecililer aktif ise yön seçimi aktiftir
        return this.element?.kutuk?.fikoSecimYuzey?.aktif === true;
    }

    /**
     * Kapanmayı kontrol et - yön seçimi aktif ise engelle
     */
    kapamaKontrol(): boolean {
        if (this.isYonSecimiAktif()) {
            // Yön seçimi aktifse, uyarı göster ve kapanmayı engelle
            alert('⚠️ Yön seçimi devam ediyor!\n\nLütfen yüzey seçimini tamamlayın veya "↻ Sıfırla" butonuna tıklayın.');
            return false; // ❌ Kapanmayı ENGELLE
        }
        return true; // ✅ Kapanmaya izin ver
    }

    /**
     * Bileşen destroy olmadan önce kontrol et
     * Yön seçimi aktif olduğu sürece kapanmaya izin verme
     */
    ngOnDestroy(): void {
        // Yön seçimi aktif ise yüzey seçimini durdur
        if (this.element?.kutuk) {
            // Yüzey seçimini temiz bir şekilde durdur
            this.element.kutuk.yuzeySecimiDurdur();
        }
    }
}

export class FikoCizimYalin extends FikoGrup {
    static cizgiMaterial = new LineBasicMaterial({ linewidth: 1, polygonOffset: true, polygonOffsetUnits: 1, polygonOffsetFactor: 1, side: THREE.FrontSide, vertexColors: true, premultipliedAlpha: true, transparent: false });

    // ==================== PERFORMANCE OPTIMIZATION: MATERIAL CACHE ====================
    /**
     * Static material cache - shared materials for FikoCizimYalin instances
     * Reduces allocations when multiple instances have identical material properties
     * Cache key: "vertexColors_side_emissive" (e.g., "true_2_0")
     */
    private static _materialCache = new Map<string, THREE.MeshPhongMaterial>();

    /**
     * Get or create a shared MeshPhongMaterial with specified properties
     * Materials are cached and reused across FikoCizimYalin instances
     * @param vertexColors Whether to use vertex colors
     * @param side Material side (THREE.DoubleSide, etc.)
     * @param emissive Emissive color hex value
     * @returns Cached or newly created material
     */
    private static getSharedMaterial(
        vertexColors: boolean,
        side: THREE.Side = THREE.DoubleSide,
        emissive: number = 0x0
    ): THREE.MeshPhongMaterial {
        const key = `${vertexColors ? 1 : 0}_${side}_${emissive}`;
        let mat = this._materialCache.get(key);
        if (!mat) {
            mat = new THREE.MeshPhongMaterial({
                vertexColors: vertexColors,
                side: side,
                emissive: emissive
            });
            this._materialCache.set(key, mat);
        }
        return mat;
    }

    /**
     * Clear the static material cache
     * Call after large import operations to free memory
     */
    public static clearMaterialCache(): void {
        // Dispose all cached materials
        this._materialCache.forEach(mat => mat.dispose());
        this._materialCache.clear();
    }
    // ==================== END MATERIAL CACHE ====================

    /** */
    override type = "FikoCizimYalin";

    /** ✅ Menüyü kapatmayı engelle flag'ı - yön seçimi aktif olduğunda true */
    kapatmayiEngelle: boolean = false;

    /** Kalın çizgi kullanılacak mı? (LineSegments2 ile gerçek kalınlık desteği) */
    kalinCizgiKullan: boolean = false;
    /** Çizgi kalınlığı (piksel cinsinden, kalinCizgiKullan=true ise çalışır) */
    cizgiKalinligi: number = 2;

    /** Kamera konumları - FikoCizimYalin'a özel kamera pozisyonları */
    konumlar: FikoKonumlar;

    /** İşaretleyiciler - FikoCizimYalin'a özel 3D yazı işaretleyicileri (DEVRE DIŞI) */
    isaretleyiciler: Fiko3DIsaretleyiciler | null = null;

    /** Kütük - FikoCizimYalin'a özel Kütük grubu */
    kutuk: FikoKutuk;

    /** BREP Topoloji Bilgisi - Mesh-to-BREP analiz sonucu */
    private _brepAnalysis: MeshAnalysisResult | null = null;
    private _brepDirty: boolean = true; // BREP yeniden hesaplanmalı mı

    // PERFORMANS: Cached property values to avoid geometry cloning on every access
    private _alanCache: number | null = null;
    private _hacimCache: number | null = null;
    private _agirlikCache: number | null = null;
    private _lastCacheTime: number = 0;
    private readonly CACHE_DURATION_MS = 5000; // Cache geçerliliği: 5 saniye

    // PERFORMANS: Non-indexed geometry cache
    private _nonIndexedGeomCache: THREE.BufferGeometry | null = null;
    private _nonIndexedGeomCacheValid: boolean = false;

    // 🚀 PERFORMANS: Raycast children cache - filter/find her çağrıda yapılmasın
    private _raycastMeshListCache: THREE.Object3D[] | null = null;
    private _raycastLineSegments2Cache: any = null;
    private _raycastPointsCache: any = null;
    private _raycastCacheDirty: boolean = true;

    // 🚀 PERFORMANS: Pre-allocated vectors for decompose - allocation overhead yok
    private static _decomposePos = new THREE.Vector3();
    private static _decomposeQuat = new THREE.Quaternion();
    private static _decomposeScale = new THREE.Vector3();

    /** Alan hesaplama (m²) - Indexed geometry için */
    public get alan(): number {
        // PERFORMANS: Cache check
        const now = Date.now();
        if (this._alanCache !== null && (now - this._lastCacheTime) < this.CACHE_DURATION_MS) {
            return this._alanCache;
        }

        if (!this.meshGeom || !this.meshGeom.attributes['position']) {
            return 0;
        }

        try {
            // PERFORMANS: Use cached non-indexed geometry instead of cloning every time
            let geom = this.meshGeom;
            if (this.meshGeom.index !== null) {
                if (!this._nonIndexedGeomCacheValid) {
                    if (this._nonIndexedGeomCache) {
                        this._nonIndexedGeomCache.dispose();
                    }
                    this._nonIndexedGeomCache = this.meshGeom.toNonIndexed();
                    this._nonIndexedGeomCacheValid = true;
                }
                geom = this._nonIndexedGeomCache;
            }

            let alan = hesaplaAlan(geom);

            // 🚀 PERFORMANS: updateMatrixWorld sadece gerektiğinde çağır
            // matrixWorld zaten render sırasında güncelleniyor
            // Sadece ilk kullanımda veya transform değişikliğinde gerekli
            if (this.matrixWorldNeedsUpdate || !this.matrixWorld.elements[0]) {
                this.updateMatrixWorld(false); // false = sadece bu node, recursive değil
            }
            // 🚀 PERFORMANS: Pre-allocated static vectors kullan - allocation overhead yok
            this.matrixWorld.decompose(FikoCizimYalin._decomposePos, FikoCizimYalin._decomposeQuat, FikoCizimYalin._decomposeScale);
            const worldScale = FikoCizimYalin._decomposeScale;

            // Alan için scale faktörü: uniform scale varsayımıyla x*y kullan
            const scaleFactor = worldScale.x * worldScale.y;

            alan = alan * 1000000; // mm² cinsinden
            alan = alan * scaleFactor; // Scale faktörünü uygula
            alan = alan / 100; // cm² cinsinden

            // PERFORMANS: Cache the result
            this._alanCache = isNaN(alan) ? 0 : alan;
            this._lastCacheTime = now;

            return this._alanCache;
        } catch (error) {
            return 0;
        }
    }

    /** Hacim hesaplama (cm³) - Manuel değer veya geometry'den hesapla */
    public get hacim(): number {
        // Manuel hacim değeri varsa kullan (userData'da cm³ cinsinden)
        if (this.userData['realVolumeCm3'] !== undefined) {
            return this.userData['realVolumeCm3'];
        }

        // BRepGProp ile hesaplanan native hacim varsa kullan (çok daha doğru!)
        // OpenCascade.js worker'dan gelen BREP native hacim hesaplaması
        if (this.userData['nativeVolumeCm3'] !== undefined && this.userData['nativeVolumeCm3'] !== null) {
            const nativeVolume = this.userData['nativeVolumeCm3'];
            // Scale faktörünü uygula
            if (this.matrixWorldNeedsUpdate || !this.matrixWorld.elements[0]) {
                this.updateMatrixWorld(false);
            }
            this.matrixWorld.decompose(FikoCizimYalin._decomposePos, FikoCizimYalin._decomposeQuat, FikoCizimYalin._decomposeScale);
            const worldScale = FikoCizimYalin._decomposeScale;
            const scaleFactor = worldScale.x * worldScale.y * worldScale.z;
            const scaledVolume = nativeVolume * scaleFactor;
            return scaledVolume;
        }

        // PERFORMANS: Cache check
        const now = Date.now();
        if (this._hacimCache !== null && (now - this._lastCacheTime) < this.CACHE_DURATION_MS) {
            return this._hacimCache;
        }

        // Geometry'den hesapla (fallback - winding order sorunları olabilir)
        if (!this.meshGeom || !this.meshGeom.attributes['position']) {
            return 0;
        }

        try {
            // PERFORMANS: Use cached non-indexed geometry instead of cloning every time
            let geom = this.meshGeom;
            if (this.meshGeom.index !== null) {
                if (!this._nonIndexedGeomCacheValid) {
                    if (this._nonIndexedGeomCache) {
                        this._nonIndexedGeomCache.dispose();
                    }
                    this._nonIndexedGeomCache = this.meshGeom.toNonIndexed();
                    this._nonIndexedGeomCacheValid = true;
                }
                geom = this._nonIndexedGeomCache;
            }

            let hacim = hesaplaHacim(geom);

            // 🚀 PERFORMANS: updateMatrixWorld sadece gerektiğinde çağır
            if (this.matrixWorldNeedsUpdate || !this.matrixWorld.elements[0]) {
                this.updateMatrixWorld(false); // false = sadece bu node, recursive değil
            }
            // 🚀 PERFORMANS: Pre-allocated static vectors kullan - allocation overhead yok
            this.matrixWorld.decompose(FikoCizimYalin._decomposePos, FikoCizimYalin._decomposeQuat, FikoCizimYalin._decomposeScale);
            const worldScale = FikoCizimYalin._decomposeScale;

            // Hacim 3 boyutlu olduğu için scale'in 3 ekseninin çarpımını kullanıyoruz
            const scaleFactor = worldScale.x * worldScale.y * worldScale.z;

            hacim = hacim * 100000; // mm³ cinsinden
            hacim = hacim * scaleFactor; // Scale faktörünü uygula
            hacim = hacim / 1000; // cm³ cinsinden

            // Eğer userData'da manuel düzeltme faktörü varsa uygula
            const volumeCorrectionFactor = this.userData['volumeCorrectionFactor'];
            if (volumeCorrectionFactor !== undefined) {
                hacim = hacim * volumeCorrectionFactor;
            }

            // PERFORMANS: Cache the result
            this._hacimCache = isNaN(hacim) ? 0 : hacim;
            this._lastCacheTime = now;

            return this._hacimCache;
        } catch (error) {
            return 0;
        }
    }

    /** Ağırlık hesaplama (kg) - Manuel değer veya hacimden hesapla */
    public get agirlik(): number {
        // Manuel ağırlık değeri varsa kullan (userData'da kg cinsinden)
        if (this.userData['realWeightKg'] !== undefined) {
            return this.userData['realWeightKg'];
        }

        // PERFORMANS: Cache check
        const now = Date.now();
        if (this._agirlikCache !== null && (now - this._lastCacheTime) < this.CACHE_DURATION_MS) {
            return this._agirlikCache;
        }

        // Hacimden hesapla
        // hacim cm³ cinsinden geliyor (scale faktörü dahil)
        const hacimCm3 = this.hacim; // cm³ cinsinden

        // userData'dan density al, yoksa Alüminyum default değerini kullan
        // Alüminyum özkütle: 2.71 g/cm³ = 0.00271 kg/cm³
        let ozKutleKgCm3 = this.userData['densityKgCm3'] !== undefined
            ? this.userData['densityKgCm3']
            : 0.00271; // Default: Alüminyum

        const agirlik = hesaplaAgirlik(hacimCm3, ozKutleKgCm3);

        // PERFORMANS: Cache the result
        this._agirlikCache = isNaN(agirlik) ? 0 : agirlik;
        this._lastCacheTime = now;

        return this._agirlikCache;
    }

    /** Getter: Mevcut density değerini döndür */
    public get density(): number {
        return this.userData['densityKgCm3'] !== undefined
            ? this.userData['densityKgCm3']
            : 0.00271; // Default: Alüminyum (2.71 g/cm³)
    }

    /** Setter: Density değerini ayarla ve cache'i invalidate et */
    public set density(value: number) {
        this.userData['densityKgCm3'] = value;
        this.invalidatePropertyCache(); // Cache'i sıfırla
    }

    /** PERFORMANS: Cache'i invalidate et (geometry değiştiğinde çağrılmalı) */
    public invalidatePropertyCache(): void {
        this._alanCache = null;
        this._hacimCache = null;
        this._agirlikCache = null;
        this._nonIndexedGeomCacheValid = false;
        this._lastCacheTime = 0;
        this._brepDirty = true; // BREP de yeniden hesaplanmalı
    }

    // ========== BREP TOPOLOJI SISTEMI ==========

    /**
     * BREP Analizi - Mesh'ten BREP topolojisi çıkarır (WASM-accelerated)
     * @returns Topolojik analiz sonucu (vertex, edge, face, shell, solid bilgileri)
     */
    public async getBrepAnalysis(): Promise<MeshAnalysisResult | null> {
        if (!this._brepDirty && this._brepAnalysis !== null) {
            return this._brepAnalysis; // Cache'den dön
        }

        if (!this.meshGeom || !this.meshGeom.attributes['position']) {
            return null;
        }

        try {
            const brepEngine = new FikoBrep();

            // Mesh-to-BREP analizi yap (WASM-accelerated, grup bazlı)
            this._brepAnalysis = await brepEngine.meshtenBrepOlustur(this.meshGeom, {
                tolerance: 1e-6,
                recognizeSurfaces: true,  // Yüzey tanıma (plane, cylinder, sphere)
                recognizeCurves: true,    // Eğri tanıma (line, arc, circle)
                simplify: false,          // Basitleştirme yapma (hassas analiz)
                mergeCoplanarFaces: false, // Merged geometry için false
                useGroups: true           // Grup attribute'larını kullan
            });

            this._brepDirty = false;
            return this._brepAnalysis;
        } catch (error) {
            return null;
        }
    }

    /**
     * BREP Analizi (senkron getter - Promise döner)
     * Kullanım: await cizim.brepAnalysis
     */
    public get brepAnalysis(): Promise<MeshAnalysisResult | null> {
        return this.getBrepAnalysis();
    }

    /**
     * BREP Topoloji Verisi - Vertex, Edge, Face, Shell, Solid bilgileri
     */
    public async brepBody(): Promise<BrepBody | null> {
        const analysis = await this.brepAnalysis;
        return analysis?.brep ?? null;
    }

    /**
     * BREP İstatistikleri - Vertex/Edge/Face sayıları, hacim, alan
     */
    public async brepStats() {
        const brep = await this.brepBody();
        if (!brep) return null;

        return {
            vertices: brep.stats.vertexCount,
            edges: brep.stats.edgeCount,
            faces: brep.stats.faceCount,
            shells: brep.stats.shellCount,
            solids: brep.stats.solidCount,
            volume: brep.stats.totalVolume,
            area: brep.stats.totalArea,
            tolerance: brep.tolerance,
        };
    }

    /**
     * BREP Feature'ları - Düzlem, silindir, küre vs. yüzey tipleri
     */
    public async brepFeatures() {
        const analysis = await this.brepAnalysis;
        if (!analysis) return null;

        return {
            planes: analysis.features.planes.length,
            cylinders: analysis.features.cylinders.length,
            spheres: analysis.features.spheres.length,
            cones: analysis.features.cones.length,
            freeform: analysis.features.freeform.length,
        };
    }

    /**
     * BREP Topoloji Bilgisi - Manifold, watertight, genus
     */
    public async brepTopology() {
        const analysis = await this.brepAnalysis;
        if (!analysis) return null;

        return {
            manifold: analysis.topology.manifold,
            watertight: analysis.topology.watertight,
            orientable: analysis.topology.orientable,
            eulerCharacteristic: analysis.topology.eulerCharacteristic,
            genus: analysis.topology.genus,
        };
    }

    /**
     * BREP Geometrik Özellikler - Convex/concave/sharp edges
     */
    public async brepGeometry() {
        const analysis = await this.brepAnalysis;
        if (!analysis) return null;

        return {
            convexEdges: analysis.geometry.convexEdges.length,
            concaveEdges: analysis.geometry.concaveEdges.length,
            sharpEdges: analysis.geometry.sharpEdges.length,
            smoothEdges: analysis.geometry.smoothEdges.length,
        };
    }

    /**
     * BREP Analizini Zorla Yeniden Hesapla
     */
    public async recomputeBrep(): Promise<void> {
        this._brepDirty = true;
        this._brepAnalysis = null;
        // Analizi tetikle
        await this.getBrepAnalysis();
    }

    /**
     * BREP Verilerini Konsola Yazdır (Debug)
     */
    public async debugBrep(): Promise<void> {
        if (!FikoThreeJsComponent.DEBUG_MODE) return;

        console.group(`[FikoCizimYalin] BREP Analiz: ${this.name}`);

        const stats = await this.brepStats();
        if (stats) {
            FikoThreeJsComponent.debugLog('📊 İstatistikler:', stats);
        }

        const features = await this.brepFeatures();
        if (features) {
            FikoThreeJsComponent.debugLog('🎨 Feature\'lar:', features);
        }

        const topology = await this.brepTopology();
        if (topology) {
            FikoThreeJsComponent.debugLog('🔗 Topoloji:', topology);
        }

        const geometry = await this.brepGeometry();
        if (geometry) {
            FikoThreeJsComponent.debugLog('📐 Geometri:', geometry);
        }

        console.groupEnd();
    }

    // ========== GPU-ACCELERATED GRUP BAZLI BREP EXTRACTION ==========

    /** GPU BREP Extractor instance (lazy-loaded) */
    private _gpuBrepExtractor: FikoGPUBrepExtractor | null = null;

    /** Grup bazlı BREP analiz sonuçları cache'i */
    private _grupBrepCache: GrupBrepResult[] | null = null;
    private _grupBrepCacheValid: boolean = false;

    /**
     * 🚀 GPU-Accelerated Grup Bazlı BREP Analizi
     *
     * Her grubun ayrı ayrı BRep ve topology bilgilerini çıkarır.
     * WebGPU destekleniyorsa GPU ile, yoksa optimize edilmiş CPU ile çalışır.
     *
     * @param options Analiz seçenekleri
     * @returns Her grup için ayrı GrupBrepResult
     */
    public async grupBrepAnaliz(options?: {
        tolerance?: number;
        recognizeSurfaces?: boolean;
        detectFeatures?: boolean;
        computeCurvature?: boolean;
        forceRecompute?: boolean;
    }): Promise<GrupBrepResult[]> {
        // Cache kontrolü
        if (!options?.forceRecompute && this._grupBrepCacheValid && this._grupBrepCache) {
            return this._grupBrepCache;
        }

        if (!this.meshGeom || !this.meshGeom.attributes['position']) {
            return [];
        }

        // GPU BREP Extractor'ı lazy-load et
        if (!this._gpuBrepExtractor) {
            this._gpuBrepExtractor = new FikoGPUBrepExtractor(options?.tolerance ?? 1e-6);
        }

        const startTime = performance.now();

        try {
            // GPU ile grup bazlı BREP extraction
            this._grupBrepCache = await this._gpuBrepExtractor.extractBrepByGroups(
                this.meshGeom,
                {
                    tolerance: options?.tolerance,
                    recognizeSurfaces: options?.recognizeSurfaces ?? true,
                    detectFeatures: options?.detectFeatures ?? true,
                    computeCurvature: options?.computeCurvature ?? false,
                }
            );

            this._grupBrepCacheValid = true;

            const totalTime = performance.now() - startTime;

            return this._grupBrepCache;

        } catch (error) {
            return [];
        }
    }

    /**
     * 📊 Belirli bir grubun BREP sonucunu al
     */
    public async getGrupBrep(grupId: number): Promise<GrupBrepResult | null> {
        const results = await this.grupBrepAnaliz();
        return results.find(r => r.grupId === grupId) || null;
    }

    /**
     * 🔬 Tüm grupların topology özetini al
     */
    public async getGruplarTopologyOzet(): Promise<{
        grupId: number;
        vertexCount: number;
        edgeCount: number;
        faceCount: number;
        eulerCharacteristic: number;
        manifold: boolean;
        watertight: boolean;
        volume: number;
        surfaceArea: number;
    }[]> {
        const results = await this.grupBrepAnaliz();

        return results.map(r => ({
            grupId: r.grupId,
            vertexCount: r.topology.vertexCount,
            edgeCount: r.topology.edgeCount,
            faceCount: r.topology.faceCount,
            eulerCharacteristic: r.topology.eulerCharacteristic,
            manifold: r.topology.manifold,
            watertight: r.topology.watertight,
            volume: r.geometry.volume,
            surfaceArea: r.geometry.surfaceArea,
        }));
    }

    /**
     * 🎨 Tüm grupların yüzey tipleri özeti
     */
    public async getGruplarSurfaceOzet(): Promise<{
        grupId: number;
        planes: number;
        cylinders: number;
        spheres: number;
        cones: number;
        tori: number;
        freeform: number;
    }[]> {
        const results = await this.grupBrepAnaliz();

        return results.map(r => ({
            grupId: r.grupId,
            planes: r.surfaces.planes.length,
            cylinders: r.surfaces.cylinders.length,
            spheres: r.surfaces.spheres.length,
            cones: r.surfaces.cones.length,
            tori: r.surfaces.tori.length,
            freeform: r.surfaces.freeform.length,
        }));
    }

    /**
     * 🔧 Tüm grupların feature'ları özeti (delik, cep, fillet, chamfer)
     */
    public async getGruplarFeatureOzet(): Promise<{
        grupId: number;
        holes: number;
        pockets: number;
        bosses: number;
        fillets: number;
        chamfers: number;
        ribs: number;
    }[]> {
        const results = await this.grupBrepAnaliz();

        return results.map(r => ({
            grupId: r.grupId,
            holes: r.features.holes.length,
            pockets: r.features.pockets.length,
            bosses: r.features.bosses.length,
            fillets: r.features.fillets.length,
            chamfers: r.features.chamfers.length,
            ribs: r.features.ribs.length,
        }));
    }

    /**
     * 📐 Belirli bir grubun geometrik özelliklerini al
     */
    public async getGrupGeometry(grupId: number): Promise<{
        boundingBox: THREE.Box3;
        volume: number;
        surfaceArea: number;
        centerOfMass: THREE.Vector3;
        curvature: {
            min: number;
            max: number;
            avg: number;
            gaussian: number;
            mean: number;
        };
        edges: {
            sharp: number;
            smooth: number;
            convex: number;
            concave: number;
            totalLength: number;
        };
    } | null> {
        const grupBrep = await this.getGrupBrep(grupId);
        if (!grupBrep) return null;

        return {
            boundingBox: grupBrep.geometry.boundingBox,
            volume: grupBrep.geometry.volume,
            surfaceArea: grupBrep.geometry.surfaceArea,
            centerOfMass: grupBrep.geometry.centerOfMass,
            curvature: {
                min: grupBrep.geometry.minCurvature,
                max: grupBrep.geometry.maxCurvature,
                avg: grupBrep.geometry.avgCurvature,
                gaussian: grupBrep.geometry.gaussianCurvature,
                mean: grupBrep.geometry.meanCurvature,
            },
            edges: {
                sharp: grupBrep.geometry.sharpEdges,
                smooth: grupBrep.geometry.smoothEdges,
                convex: grupBrep.geometry.convexEdges,
                concave: grupBrep.geometry.concaveEdges,
                totalLength: grupBrep.geometry.totalEdgeLength,
            }
        };
    }

    /**
     * 🕳️ Belirli bir grubun deliklerini al
     */
    public async getGrupDelikleri(grupId: number): Promise<{
        id: string;
        type: string;
        center: THREE.Vector3;
        axis: THREE.Vector3;
        diameter: number;
        depth: number;
    }[]> {
        const grupBrep = await this.getGrupBrep(grupId);
        if (!grupBrep) return [];

        return grupBrep.features.holes.map(h => ({
            id: h.id,
            type: h.type,
            center: h.center,
            axis: h.axis,
            diameter: h.diameter,
            depth: h.depth,
        }));
    }

    /**
     * 📋 Detaylı BREP raporu oluştur (debug/export için)
     */
    public async generateBrepReport(): Promise<string> {
        const results = await this.grupBrepAnaliz();

        let report = `
╔══════════════════════════════════════════════════════════════════╗
║               FikoCizimYalin BREP Analysis Report                ║
║                        ${this.name}
╠══════════════════════════════════════════════════════════════════╣
║  Toplam Grup Sayısı: ${results.length.toString().padStart(5)}                                    ║
╚══════════════════════════════════════════════════════════════════╝
`;

        for (const grup of results) {
            report += `
┌──────────────────────────────────────────────────────────────────┐
│  GRUP ${grup.grupId.toString().padStart(3)}                                                        │
├──────────────────────────────────────────────────────────────────┤
│  TOPOLOGY                                                        │
│    Vertices: ${grup.topology.vertexCount.toString().padStart(8)}    Edges: ${grup.topology.edgeCount.toString().padStart(8)}              │
│    Faces: ${grup.topology.faceCount.toString().padStart(10)}      Shells: ${grup.topology.shellCount.toString().padStart(6)}              │
│    Euler: ${grup.topology.eulerCharacteristic.toString().padStart(10)}      Genus: ${grup.topology.genus.toString().padStart(7)}              │
│    Manifold: ${grup.topology.manifold ? 'Evet' : 'Hayır'}          Watertight: ${grup.topology.watertight ? 'Evet' : 'Hayır'}       │
├──────────────────────────────────────────────────────────────────┤
│  GEOMETRY                                                        │
│    Volume: ${grup.geometry.volume.toFixed(4).padStart(12)} cm³                              │
│    Surface Area: ${grup.geometry.surfaceArea.toFixed(4).padStart(12)} cm²                        │
│    Edge Length: ${grup.geometry.totalEdgeLength.toFixed(4).padStart(12)} mm                       │
├──────────────────────────────────────────────────────────────────┤
│  SURFACES                                                        │
│    Planes: ${grup.surfaces.planes.length.toString().padStart(6)}   Cylinders: ${grup.surfaces.cylinders.length.toString().padStart(6)}              │
│    Spheres: ${grup.surfaces.spheres.length.toString().padStart(5)}   Cones: ${grup.surfaces.cones.length.toString().padStart(10)}              │
│    Tori: ${grup.surfaces.tori.length.toString().padStart(8)}   Freeform: ${grup.surfaces.freeform.length.toString().padStart(7)}              │
├──────────────────────────────────────────────────────────────────┤
│  FEATURES                                                        │
│    Holes: ${grup.features.holes.length.toString().padStart(7)}   Fillets: ${grup.features.fillets.length.toString().padStart(8)}              │
│    Chamfers: ${grup.features.chamfers.length.toString().padStart(5)}   Pockets: ${grup.features.pockets.length.toString().padStart(8)}              │
├──────────────────────────────────────────────────────────────────┤
│  STATS                                                           │
│    Processing Time: ${grup.stats.processingTimeMs.toFixed(2).padStart(8)} ms                          │
│    GPU Accelerated: ${grup.stats.gpuAccelerated ? 'Evet' : 'Hayır'}                                │
│    Triangle Count: ${grup.stats.triangleCount.toString().padStart(9)}                              │
└──────────────────────────────────────────────────────────────────┘
`;
        }

        return report;
    }

    /**
     * GPU BREP cache'ini invalidate et
     */
    public invalidateGrupBrepCache(): void {
        this._grupBrepCacheValid = false;
        this._grupBrepCache = null;
    }

    /**
     * GPU BREP Extractor'ı temizle
     */
    public disposeGpuBrepExtractor(): void {
        if (this._gpuBrepExtractor) {
            this._gpuBrepExtractor.dispose();
            this._gpuBrepExtractor = null;
        }
        this.invalidateGrupBrepCache();
    }

    /** Hızlı 3D menü - basılı tutunca gösterilecek butonlar */
    hizli3DMenu = [
        {
            simge: "eye",
            baslik: "Gizle / Göster",
            renkSinif: "yellow",
            tiklandi: () => {
                this.visible = !this.visible;
                this.sahne.anaGosterim.FikoYT.degisti.emit();
                this.sahne.anaGosterim.ciz();
            }
        },
        {
            simge: "move",
            baslik: "Ekrana Sığdır",
            tiklandi: () => {
                this.sahne.ekranaSigdir(this);
            }
        },
        {
            simge: "camera",
            baslik: "Konumlar",
            tiklandi: () => {
                // Konumlar dialog'unu aç
                this.konumlar.ciftTiklandi();
            }
        },
        {
            simge: "photo",
            baslik: "Fotoğraf Çek",
            renkSinif: "green",
            tiklandi: () => {
                this.fotografCek();
            }
        },

    ];
    /** Hızlı 3D menü - basılı tutunca gösterilecek butonlar */
    solAracCubugu = [
        {
            simge: "eye",
            baslik: "Gizle / Göster",
            renkSinif: "yellow",
            tiklandi: () => {
                this.visible = !this.visible;
                this.sahne.anaGosterim.FikoYT.degisti.emit();
                this.sahne.anaGosterim.ciz();
            }
        },
        {
            simge: "move",
            baslik: "Ekrana Sığdır",
            tiklandi: () => {
                this.sahne.ekranaSigdir(this);
            }
        },

    ];
    /** Hızlı 3D menü - basılı tutunca gösterilecek component */
    hizliMenu3DEk = FikoCizimYalinEkBilesen;
    /** Hızlı 3D menü - basılı tutunca gösterilecek bilgiler */
    hizliMenu3DEkParametreler: { element: any } = { element: this };

    sagMenu: any[] = [
        {
            simge: null,
            baslik: "Gizle / Göster",
            tiklandi: () => {
                this.visible = !this.visible;
                this.sahne.anaGosterim.FikoYT.degisti.emit();
                this.sahne.anaGosterim.ciz();
            }
        },
        {
            simgeYazi: "🖥",
            baslik: "Ekrana Sığdır",
            tiklandi: () => {
                this.sahne.ekranaSigdir(this);
            }
        },
        {
            tipi: "Ayraç"
        },
        {
            simge: "camera",
            baslik: "Konumlar",
            tiklandi: () => {
                // Konumlar dialog'unu aç
                this.konumlar.ciftTiklandi();
            }
        },
        // İşaretleyiciler menü öğesi DEVRE DIŞI
        // {
        //     simge: "flag",
        //     baslik: "İşaretleyiciler",
        //     tiklandi: () => {
        //         const yeniIsaretleyici = this.isaretleyiciler.ekleFikoIsaretci();
        //         const merkez = this.getAgirlikMerkezi();
        //         yeniIsaretleyici.position.copy(merkez);
        //         this.sahne.anaGosterim.ciz();
        //         this.sahne.anaGosterim.FikoYT.degisti.emit();
        //     }
        // },
        {
            tipi: "Ayraç"
        },
        {
            simgeYazi: "🔬",
            baslik: "BREP Topoloji Analizi",
            tiklandi: async () => {
                try {
                    // GPU BREP analizi yap
                    const grupSonuclari = await this.grupBrepAnaliz({
                        recognizeSurfaces: true,
                        detectFeatures: true
                    });

                    if (grupSonuclari.length === 0) {
                        alert('BREP analizi yapılamadı. Mesh geometry bulunamadı.');
                        return;
                    }

                    // Toplam istatistikleri hesapla
                    let toplamVertex = 0, toplamEdge = 0, toplamFace = 0;
                    let toplamPlane = 0, toplamCylinder = 0, toplamSphere = 0;
                    let toplamCone = 0, toplamTorus = 0, toplamFreeform = 0;
                    let toplamHole = 0, toplamFillet = 0, toplamChamfer = 0;
                    let toplamVolume = 0, toplamArea = 0;
                    let gpuAccelerated = false;
                    let totalTime = 0;

                    grupSonuclari.forEach(g => {
                        toplamVertex += g.topology.vertexCount;
                        toplamEdge += g.topology.edgeCount;
                        toplamFace += g.topology.faceCount;

                        toplamPlane += g.surfaces.planes.length;
                        toplamCylinder += g.surfaces.cylinders.length;
                        toplamSphere += g.surfaces.spheres.length;
                        toplamCone += g.surfaces.cones.length;
                        toplamTorus += g.surfaces.tori.length;
                        toplamFreeform += g.surfaces.freeform.length;

                        toplamHole += g.features.holes.length;
                        toplamFillet += g.features.fillets.length;
                        toplamChamfer += g.features.chamfers.length;

                        toplamVolume += g.geometry.volume;
                        toplamArea += g.geometry.surfaceArea;

                        gpuAccelerated = g.stats.gpuAccelerated;
                        totalTime += g.stats.processingTimeMs;
                    });

                    // Konsola detaylı rapor yaz
                    const rapor = await this.generateBrepReport();

                    // Kullanıcıya alert göster
                    const mesaj = `🔬 GPU BREP Analizi: ${this.name}\n` +
                        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
                        `📊 TOPLAM (${grupSonuclari.length} grup):\n` +
                        `  • Vertex: ${toplamVertex.toLocaleString()}\n` +
                        `  • Edge: ${toplamEdge.toLocaleString()}\n` +
                        `  • Face: ${toplamFace.toLocaleString()}\n\n` +
                        `📐 GEOMETRİ:\n` +
                        `  • Hacim: ${toplamVolume.toFixed(2)} cm³\n` +
                        `  • Alan: ${toplamArea.toFixed(2)} cm²\n\n` +
                        `🎨 YÜZEY TANIMA:\n` +
                        `  • Düzlem: ${toplamPlane}\n` +
                        `  • Silindir: ${toplamCylinder}\n` +
                        `  • Küre: ${toplamSphere}\n` +
                        `  • Koni: ${toplamCone}\n` +
                        `  • Torus: ${toplamTorus}\n` +
                        `  • Serbest Form: ${toplamFreeform}\n\n` +
                        `🔧 FEATURE'LAR:\n` +
                        `  • Delik: ${toplamHole}\n` +
                        `  • Fillet: ${toplamFillet}\n` +
                        `  • Chamfer: ${toplamChamfer}\n\n` +
                        `⚡ PERFORMANS:\n` +
                        `  • GPU: ${gpuAccelerated ? 'Evet ✓' : 'Hayır (CPU)'}\n` +
                        `  • Süre: ${totalTime.toFixed(2)} ms\n\n` +
                        `Detaylı rapor için konsola bakınız (F12).`;

                    alert(mesaj);

                } catch (error) {
                    alert('BREP analizi sırasında hata oluştu. Konsolu kontrol edin.');
                }
            }
        },
        {
            tipi: "Ayraç"
        },
        {
            simge: null,
            baslik: "Sil '" + this.name + "'",
            tiklandi: () => {
                this.meshGeom?.dispose();
                this.edgesGeom?.dispose();
                this.vertexGeom?.dispose()

                this.parent.remove(this);

                this.sahne.anaGosterim.FikoYT.degisti.emit();
                this.sahne.anaGosterim.ciz();
            }
        },
        {
            tipi: "Ayraç"
        },
        {
            simge: null,
            baslik: "Çıkart Dosyaya '" + this.name + "'",
            tiklandi: () => {
                this.export();
            }
        },
        {
            simge: null,
            baslik: "Çek Dosyadan",
            tiklandi: () => {
                this.import();
            }
        }
    ]

    /**
     * Geometry'nin ağırlık merkezini hesaplar
     */
    private getAgirlikMerkezi(): THREE.Vector3 {
        if (!this.meshGeom || !this.meshGeom.attributes['position']) {
            return new THREE.Vector3(0, 0, 0);
        }

        const positions = this.meshGeom.attributes['position'];
        const vertexCount = positions.count;
        let centerX = 0, centerY = 0, centerZ = 0;

        // Tüm vertex'lerin ortalamasını hesapla
        for (let i = 0; i < vertexCount; i++) {
            centerX += positions.getX(i);
            centerY += positions.getY(i);
            centerZ += positions.getZ(i);
        }

        return new THREE.Vector3(
            centerX / vertexCount,
            centerY / vertexCount,
            centerZ / vertexCount
        );
    }

    /** */
    mesh: THREE.Mesh
    cizgiler: THREE.LineSegments | LineSegments2;
    noktalar: THREE.Points;
    /** */
    // meshGeom: THREE.BufferGeometry;
    // edges: THREE.LineSegments;

    constructor(public override sahne?: FikoSahne, isim?: string, public meshGeom?: THREE.BufferGeometry, public edgesGeom?: THREE.BufferGeometry, public vertexGeom?: THREE.BufferGeometry, kalinCizgiKullan: boolean = false, cizgiKalinligi: number = 2) {
        super(sahne, isim);

        meshGeom = meshGeom ?? new THREE.BufferGeometry();

        this.kalinCizgiKullan = kalinCizgiKullan;
        this.cizgiKalinligi = cizgiKalinligi;

        this.userData['FikoArayuz'].agactaGorunur = true;
        this.userData['FikoArayuz'].simge = "./simgeler/yalinCizim.webp";

        // Konumlar sistemi oluştur
        this.konumlar = new FikoKonumlar(this.sahne);
        this.konumlar.name = this.name + " Konumları";
        // Konumları ürün ağacına ekle (FikoKaynakca'daki gibi)
        this.ekle(this.konumlar);

        // İşaretleyiciler sistemi DEVRE DIŞI
        // this.isaretleyiciler = new Fiko3DIsaretleyiciler(this.sahne, this.name + " İşaretleyicileri");
        // this.ekle(this.isaretleyiciler);
        // const ilkIsaretleyici = this.isaretleyiciler.ekleFikoIsaretci();
        // ilkIsaretleyici.visible = false;
        // const merkez = this.getAgirlikMerkezi();
        // ilkIsaretleyici.position.copy(merkez);

        // Kütük sistemi oluştur
        this.kutuk = new FikoKutuk(this.sahne, this.name + " Kütük");
        this.kutuk.userData.FikoArayuz.simge = "./simgeler/kupler.webp";
        // Kutuk'u ürün ağacına ekle (bu çizimin alt elemanı olarak)
        this.ekle(this.kutuk);

        /** */
        // ⭐ Material seç: Eğer geometry'de color attribute varsa, vertex color'ları kullan
        // 🎯 FIX: Her zaman clone et ve polygonOffset ekle - edge'lerin önde görünmesi için
        // 🚀 PERFORMANCE: Use cached shared materials when baseMaterial is unavailable
        const baseMaterial = this.sahne?.anaGosterim?.standartMaterialMesh as any;
        let meshMaterial: THREE.Material;

        const hasVertexColors = this.meshGeom && this.meshGeom.getAttribute('color') !== null;
        if (baseMaterial && baseMaterial.clone) {
            meshMaterial = baseMaterial.clone();
            if (hasVertexColors) {
                (meshMaterial as any).vertexColors = true;
            }
        } else {
            // 🚀 OPTIMIZED: Use shared material cache instead of creating new instances
            // Reduces allocations when multiple FikoCizimYalin instances have identical materials
            meshMaterial = FikoCizimYalin.getSharedMaterial(hasVertexColors, THREE.DoubleSide, 0x0);
        }

        // NOT: Mesh'e polygonOffset EKLENMEZ — ince parçalarda ön/arka yüzey depth'i ters dönüp
        // GPU picker'ın arka yüzeyi seçmesine yol açıyordu. Z-fighting çözümü kenar malzemesinde
        // (kalinCizgiOlustur'da negatif polygonOffset ile edge'i öne çekerek) yapılır.

        this.mesh = new THREE.Mesh(this.meshGeom, meshMaterial)
        this.mesh.renderOrder = 0
        this.ekle(this.mesh);
        /** */
        // Kalın çizgi kullanılacaksa LineSegments2, değilse normal LineSegments
        // edgesGeom null ise boş geometry kullan
        const cizgiGeometry = this.edgesGeom ?? new THREE.BufferGeometry();

        if (this.kalinCizgiKullan) {
            this.cizgiler = this.kalinCizgiOlustur(cizgiGeometry, this.cizgiKalinligi);
        } else {
            this.cizgiler = this.kalinCizgiOlustur(cizgiGeometry, 1);
        }
        // this.cizgiler.renderOrder = -1

        this.ekle(this.cizgiler);
        /** */
        this.noktalar = new THREE.Points(this.vertexGeom, FikoThreeJsComponent.standartMaterialPointVertex);
        this.ekle(this.noktalar)
        // this.edges = new THREE.LineSegments(new THREE.BufferGeometry(), this.sahne?.anaGosterim?.standartMaterialCizgiEdge);




        this.matrixAutoUpdate = false;
        this.matrixWorldNeedsUpdate = false;

        this.kutuk.roughStockOlusturCizimIcin()
        this.kutuk.visible = false;
    }

    /** Kalın çizgi oluşturma metodu (LineSegments2 kullanarak) */
    private kalinCizgiOlustur(geometry: THREE.BufferGeometry, kalinlik: number): LineSegments2 {
        // BufferGeometry'den LineSegmentsGeometry'ye dönüştürme
        const lineSegmentsGeometry = new LineSegmentsGeometry();

        // Position verilerini al
        const positions = geometry.getAttribute('position');
        if (positions) {
            //@ts-ignore
            lineSegmentsGeometry.setPositions(positions.array as number[]);
        }

        // Renk verilerini al (varsa)
        const colors = geometry.getAttribute('color');
        if (colors) {
            //@ts-ignore
            lineSegmentsGeometry.setColors(colors.array as number[]);
        }

        // ✅ GRUP ATTRIBUTE'UNU KOPYALA - ÇOK ÖNEMLİ!
        const grup = geometry.getAttribute('grup');
        if (grup) {
            lineSegmentsGeometry.setAttribute('grup', grup.clone());
            // Grup attribute'u kopyalandı
        }

        // LineMaterial oluştur
        const lineMaterial = new LineMaterial({
            color: 0xffffff,
            linewidth: kalinlik, // Piksel cinsinden kalınlık
            vertexColors: colors ? true : false,
            dashed: false,
            alphaToCoverage: true,
            // 🎯 Z-FIGHTING FIX: Kenarı depth'te hafifçe ÖNE çek → yüzeyle coplanar kenarlar
            // (iki yüzeyin kesişim çizgileri) net üstte görünür. Yüzey/picking ETKİLENMEZ.
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2,
        });

        // Ekran çözünürlüğünü ayarla (render sırasında güncellenmeli)
        const _res = getCanvasResolution(this.sahne?.anaGosterim?.sunucu);
        lineMaterial.resolution.set(_res.width, _res.height);

        // LineSegments2 oluştur
        const line = new LineSegments2(lineSegmentsGeometry, lineMaterial);
        line.renderOrder = 0;

        return line;
    }

    // okuYuzey(position: THREE.Float32BufferAttribute, color: THREE.Float32BufferAttribute, alpha: THREE.Float32BufferAttribute, grup: THREE.Float32BufferAttribute) {
    //     this.meshGeom.geometry.setAttribute("position", position);
    //     this.meshGeom.geometry.setAttribute("color", color);
    //     this.meshGeom.geometry.setAttribute("alpha", alpha);
    //     this.meshGeom.geometry.setAttribute("grup", grup);
    // }

    /**
     * ✅ Complete Disposal - Tüm resource'ları tamamen temizle
     * Cache'leri, Geometry'leri, material'ları ve child'ları dispose et
     */
    dispose() {
        // ✅ Cache'leri temizle (memory leak önleme - ~50KB per cache)
        if (this._nonIndexedGeomCache) {
            this._nonIndexedGeomCache.dispose();
            this._nonIndexedGeomCache = null;
        }
        this._nonIndexedGeomCacheValid = false;
        this._alanCache = null;
        this._hacimCache = null;
        this._agirlikCache = null;

        // Mesh material ve geometry'ni temizle
        if (this.mesh) {
            if (this.mesh.geometry) this.mesh.geometry.dispose();
            if (this.mesh.material) {
                if (Array.isArray(this.mesh.material)) {
                    this.mesh.material.forEach(m => m.dispose?.());
                } else {
                    (this.mesh.material as THREE.Material).dispose?.();
                }
            }
        }

        // Çizgi material ve geometry'ni temizle
        if (this.cizgiler) {
            if (this.cizgiler.geometry) this.cizgiler.geometry.dispose();
            if (this.cizgiler.material) {
                if (Array.isArray(this.cizgiler.material)) {
                    this.cizgiler.material.forEach(m => m.dispose?.());
                } else {
                    (this.cizgiler.material as THREE.Material).dispose?.();
                }
            }
        }

        // Vertex noktaları material ve geometry'ni temizle
        if (this.noktalar) {
            if (this.noktalar.geometry) this.noktalar.geometry.dispose();
            if (this.noktalar.material) {
                if (Array.isArray(this.noktalar.material)) {
                    this.noktalar.material.forEach(m => m.dispose?.());
                } else {
                    (this.noktalar.material as THREE.Material).dispose?.();
                }
            }
        }

        // BufferGeometry'leri temizle
        this.meshGeom?.dispose();
        this.edgesGeom?.dispose();
        this.vertexGeom?.dispose();

        // ✅ Child object'leri dispose et (FikoKonumlar, Fiko3DIsaretleyiciler, FikoKutuk)
        if (this.konumlar && 'dispose' in this.konumlar && typeof (this.konumlar as any).dispose === 'function') {
            (this.konumlar as any).dispose();
        }
        // İşaretleyiciler DEVRE DIŞI - artık dispose edilmesi gerekmiyor
        // if (this.isaretleyiciler && 'dispose' in this.isaretleyiciler && typeof (this.isaretleyiciler as any).dispose === 'function') {
        //     (this.isaretleyiciler as any).dispose();
        // }
        if (this.kutuk && 'dispose' in this.kutuk && typeof (this.kutuk as any).dispose === 'function') {
            (this.kutuk as any).dispose();
        }

        // ✅ FikoGrup parent'tan çıkar
        if (this.parent) {
            this.parent.remove(this);
        }
    }

    /** 🚀 PERFORMANS: Children cache'i güncelle - add/remove sonrası çağrılmalı */
    invalidateRaycastCache(): void {
        this._raycastCacheDirty = true;
    }

    /** 🚀 PERFORMANS: Children cache'i güncelle */
    private updateRaycastCache(): void {
        if (!this._raycastCacheDirty) return;

        // Mesh listesi - işaretleyicileri hariç tut
        this._raycastMeshListCache = [];
        this._raycastLineSegments2Cache = null;
        this._raycastPointsCache = null;

        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            const type = child.type;

            if (type === "LineSegments2") {
                this._raycastLineSegments2Cache = child;
            } else if (type === "Points") {
                this._raycastPointsCache = child;
            } else if (type !== "Fiko3DYaziIsaretleyici" && type !== "Fiko3DIsaretleyiciler") {
                this._raycastMeshListCache.push(child);
            }
        }

        this._raycastCacheDirty = false;
    }

    /** Raycast - 3D'de tıklama ve basılı tutma için gerekli */
    override raycast(raycaster: THREE.Raycaster, intersects: THREE.Intersection[]): void {
        if (!this.visible) return;
        if (this.children.length < 1) return;

        // 🚀 PERFORMANS: Children cache'i kullan
        this.updateRaycastCache();

        // Tüm kesişimleri toplama
        let tumKesisenler: THREE.Intersection[] = [];

        // 1. Mesh raycast (yüzeyler) - cache'den al
        const meshListesi = this._raycastMeshListCache;
        if (meshListesi && meshListesi.length > 0) {
            try {
                const meshKesisenler = raycaster.intersectObjects(meshListesi, true);
                tumKesisenler.push(...meshKesisenler);
            } catch (e) {
                // Mesh raycast hatası - devam et
            }
        }

        // 2. LineSegments2 raycast (kenarlar/edge'ler) - cache'den al
        const lineSegments2 = this._raycastLineSegments2Cache;
        if (lineSegments2 && lineSegments2.visible) {
            try {
                // LineSegments2 için özel raycast parametreleri
                const originalThreshold = raycaster.params.Line?.threshold;
                if (raycaster.params.Line) {
                    raycaster.params.Line.threshold = 35; // 🎯 30 → 35 (Çizgi seçimi daha da kolay)
                }

                const lineKesisenler: THREE.Intersection[] = [];
                lineSegments2.raycast(raycaster, lineKesisenler);

                // Threshold'u geri al
                if (originalThreshold !== undefined && raycaster.params.Line) {
                    raycaster.params.Line.threshold = originalThreshold;
                }

                tumKesisenler.push(...lineKesisenler);
            } catch (e) {
                // LineSegments2 raycast hatası - devam et
            }
        }

        // 3. Points raycast (köşe noktaları) - cache'den al
        const points = this._raycastPointsCache;
        if (points && points.visible) {
            try {
                // Points için özel raycast parametreleri
                // 🎯 FIX: Nokta seçimini kolaylaştırmak için threshold artırıldı
                const originalPointsThreshold = raycaster.params.Points?.threshold;
                if (raycaster.params.Points) {
                    raycaster.params.Points.threshold = 3.0; // 🎯 2.0 → 3.0 (Nokta seçimi kolaylaştırıldı)
                }

                const pointKesisenler: THREE.Intersection[] = [];
                points.raycast(raycaster, pointKesisenler);

                // Threshold'u geri al
                if (originalPointsThreshold !== undefined && raycaster.params.Points) {
                    raycaster.params.Points.threshold = originalPointsThreshold;
                }

                tumKesisenler.push(...pointKesisenler);
            } catch (e) {
                // Points raycast hatası - devam et
            }
        }

        // En yakın kesişimi bul ve döndür
        if (tumKesisenler.length > 0) {
            // 🎯 KENAR SEÇİM ÖNCELİĞİ:
            // İki yüzeyin birleştiği kenarlarda çizgi/nokta seçimi kolaylaştırıldı
            // Tolerance içindeki çizgi/nokta kesişimleri yüzeylerden önce seçilir
            const KENAR_TOLERANSI = 2.0; // mm cinsinden kenar seçim toleransı

            // Tip önceliği: Points > LineSegments > Mesh
            const tipOnceligi = (obj: THREE.Object3D): number => {
                if (obj.type === "Points") return 0;
                if (obj.type === "LineSegments2" || obj.type === "LineSegments" || obj.type === "Line2") return 1;
                return 2; // Mesh
            };

            // Önce mesafeye göre sırala
            tumKesisenler.sort((a, b) => a.distance - b.distance);

            // En yakın mesafeyi bul
            const enYakinMesafe = tumKesisenler[0].distance;

            // Tolerance içindeki tüm kesişimleri filtrele
            const toleransIcindekiler = tumKesisenler.filter(k =>
                (k.distance - enYakinMesafe) <= KENAR_TOLERANSI
            );

            // Tolerance içindekileri tip önceliğine göre sırala
            toleransIcindekiler.sort((a, b) => {
                const oncelikFarki = tipOnceligi(a.object) - tipOnceligi(b.object);
                if (oncelikFarki !== 0) return oncelikFarki;
                return a.distance - b.distance; // Aynı tipte olanlar mesafeye göre
            });

            const enYakin = toleransIcindekiler[0];

            intersects.push({
                object: this,
                distance: enYakin.distance,
                point: enYakin.point,
                face: enYakin.face,
                faceIndex: enYakin.faceIndex,
                index: enYakin.index,
                instanceId: this.id,
                //@ts-ignore
                altObje: enYakin.object,
                //@ts-ignore
                pointOnLine: enYakin.pointOnLine, // LineSegments2 için
                grup: this,
                enUstte: false,
                secilebilir: true,
            });
        }
    }

    override toJSON(meta?: { geometries: any; materials: any; textures: any; images: any }): any {
        const _rslt: any = {
            yuzeyler: FikoCizimYalin.geometryToCompact(this.meshGeom),
            sinirlar: FikoCizimYalin.geometryToCompact(this.edgesGeom),
            noktalar: FikoCizimYalin.geometryToCompact(this.vertexGeom),
            format: _activeBinaryCollector ? 'binary' : 'base64',
            matrix: this.matrix.toArray(),
            isim: this.name,
            type: this.type,
            uuid: this.uuid,
            visible: this.visible,
            userData: this._serializeUserData(),
            konumlar: this.konumlar.toJSON(),
        };
        return _rslt;
    }

    /** Binary toplama modunu başlat — toJSON() çağrıları _bin referanslar üretir */
    static startBinaryCollect(): void {
        _activeBinaryCollector = [];
    }

    /** Binary toplama modunu bitir ve buffer'ları döndür */
    static endBinaryCollect(): Uint8Array[] {
        const result = _activeBinaryCollector || [];
        _activeBinaryCollector = null;
        return result;
    }

    /** Aktif binary-collect'e ham byte ekler, _bin index döndürür. Toplama kapalıysa -1 (çağıran base64'e düşmeli). */
    static collectBinary(bytes: Uint8Array): number {
        if (!_activeBinaryCollector) return -1;
        _activeBinaryCollector.push(bytes.slice());
        return _activeBinaryCollector.length - 1;
    }

    /** Uint8Array → base64 (binary-collect yoksa fallback). */
    static bytesToBase64(bytes: Uint8Array): string {
        const CHUNK = 32768; let binary = '';
        for (let i = 0; i < bytes.length; i += CHUNK) {
            binary += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + CHUNK, bytes.length)) as any);
        }
        return btoa(binary);
    }

    /** base64 → Uint8Array. */
    static base64ToBytes(b64: string): Uint8Array {
        const binary = atob(b64); const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return bytes;
    }

    /** BufferGeometry → compact format (binary veya base64) */
    private static geometryToCompact(geom: THREE.BufferGeometry): any {
        const result: any = { attributes: {} };
        for (const name in geom.attributes) {
            // Binary mode: normal'ları kaydetme — yüklerken yeniden hesaplanır
            if (_activeBinaryCollector && name === 'normal') {
                result._normalsOmitted = true;
                continue;
            }

            const attr = geom.getAttribute(name);
            if (_activeBinaryCollector) {
                // Binary mode: agresif optimizasyonlar
                let bytes: Uint8Array;
                let type = attr.array.constructor.name;

                if (name === 'color' && attr.array instanceof Float32Array) {
                    // Renkleri Float32 → Uint8 + delta encoding (4x küçülme + gzip dostu)
                    const f32 = attr.array as Float32Array;
                    const u8 = new Uint8Array(f32.length);
                    for (let i = 0; i < f32.length; i++) {
                        u8[i] = Math.round(Math.min(1, Math.max(0, f32[i])) * 255);
                    }
                    // Delta encode (kanal bazlı): aynı renk bölgelerinde delta≈0 → gzip cenneti
                    const itemSize = attr.itemSize;
                    const delta = new Uint8Array(u8.length);
                    for (let c = 0; c < itemSize; c++) delta[c] = u8[c];
                    for (let i = itemSize; i < u8.length; i++) {
                        delta[i] = (u8[i] - u8[i - itemSize]) & 0xFF;
                    }
                    bytes = delta;
                    type = 'Uint8Array';
                    result.attributes[name] = {
                        itemSize, type, _bin: _activeBinaryCollector.length,
                        normalized: true, _quantized: 'u8color_delta',
                    };
                } else {
                    // Raw binary — gzip zaten iyi sıkıştırır
                    bytes = new Uint8Array(attr.array.buffer, attr.array.byteOffset, attr.array.byteLength).slice();
                    result.attributes[name] = {
                        itemSize: attr.itemSize, type, _bin: _activeBinaryCollector.length,
                        normalized: attr.normalized || false,
                    };
                }
                _activeBinaryCollector.push(bytes);
            } else {
                // Base64 mode (geriye uyumluluk)
                result.attributes[name] = {
                    itemSize: attr.itemSize,
                    type: attr.array.constructor.name,
                    base64: FikoCizimYalin.typedArrayToBase64(attr.array as any),
                    normalized: attr.normalized || false,
                };
            }
        }
        if (geom.index) {
            if (_activeBinaryCollector) {
                const idxArr = geom.index.array;
                // Index Uint32 → Uint16 indir (max < 65536 ise, 2x küçülme)
                let maxIdx = 0;
                for (let i = 0; i < idxArr.length; i++) {
                    if (idxArr[i] > maxIdx) maxIdx = idxArr[i];
                }
                if (maxIdx < 65536 && idxArr instanceof Uint32Array) {
                    // Uint32 → Uint16 indir (2x küçülme)
                    const u16 = new Uint16Array(idxArr.length);
                    for (let i = 0; i < idxArr.length; i++) u16[i] = idxArr[i];
                    const raw = new Uint8Array(u16.buffer, u16.byteOffset, u16.byteLength);
                    _activeBinaryCollector.push(raw);
                    result.index = { type: 'Uint16Array', _bin: _activeBinaryCollector.length - 1, _downcast: true };
                } else {
                    const raw = new Uint8Array(idxArr.buffer, idxArr.byteOffset, idxArr.byteLength).slice();
                    _activeBinaryCollector.push(raw);
                    result.index = { type: idxArr.constructor.name, _bin: _activeBinaryCollector.length - 1 };
                }
            } else {
                result.index = {
                    type: geom.index.array.constructor.name,
                    base64: FikoCizimYalin.typedArrayToBase64(geom.index.array as any),
                };
            }
        }
        return result;
    }

    /** Compact base64 → BufferGeometry */
    private static compactToGeometry(data: any): THREE.BufferGeometry {
        const geom = new THREE.BufferGeometry();
        if (data.attributes) {
            for (const name in data.attributes) {
                const a = data.attributes[name];
                const arr = FikoCizimYalin.base64ToTypedArray(a.base64, a.type);
                geom.setAttribute(name, new THREE.BufferAttribute(arr, a.itemSize, a.normalized || false));
            }
        }
        if (data.index) {
            const arr = FikoCizimYalin.base64ToTypedArray(data.index.base64, data.index.type);
            geom.setIndex(new THREE.BufferAttribute(arr as any, 1));
        }
        return geom;
    }

    /** Binary buffer referanslarından BufferGeometry oluştur */
    static binaryToGeometry(data: any, buffers: Uint8Array[]): THREE.BufferGeometry {
        const geom = new THREE.BufferGeometry();
        if (data.attributes) {
            for (const name in data.attributes) {
                const a = data.attributes[name];
                let arr: any;
                if (a._bin !== undefined && buffers[a._bin]) {
                    if (a._quantized === 'u16pos_delta') {
                        // Undo delta + re-interleave + dequantize
                        const encoded = FikoCizimYalin.bufferToTypedArray(buffers[a._bin], 'Uint16Array') as Uint16Array;
                        const vtxCount = a._vtxCount;
                        const [minX, minY, minZ, rX, rY, rZ] = a._bbox;

                        // 1) Undo delta per channel (cumulative sum, wrapping)
                        const xOff = 0, yOff = vtxCount, zOff = vtxCount * 2;
                        for (let ch = 0; ch < 3; ch++) {
                            const off = ch * vtxCount;
                            for (let v = 1; v < vtxCount; v++) {
                                encoded[off + v] = (encoded[off + v] + encoded[off + v - 1]) & 0xFFFF;
                            }
                        }

                        // 2) Re-interleave + dequantize → Float32
                        const f32 = new Float32Array(vtxCount * 3);
                        for (let v = 0; v < vtxCount; v++) {
                            f32[v * 3]     = (encoded[xOff + v] / 65535) * rX + minX;
                            f32[v * 3 + 1] = (encoded[yOff + v] / 65535) * rY + minY;
                            f32[v * 3 + 2] = (encoded[zOff + v] / 65535) * rZ + minZ;
                        }
                        arr = f32;
                    } else if (a._quantized === 'u16pos') {
                        // Eski format: sadece quantize (delta yok)
                        const raw = FikoCizimYalin.bufferToTypedArray(buffers[a._bin], 'Uint16Array') as Uint16Array;
                        const f32 = new Float32Array(raw.length);
                        const [minX, minY, minZ, rX, rY, rZ] = a._bbox;
                        for (let i = 0; i < raw.length; i += 3) {
                            f32[i]     = (raw[i]     / 65535) * rX + minX;
                            f32[i + 1] = (raw[i + 1] / 65535) * rY + minY;
                            f32[i + 2] = (raw[i + 2] / 65535) * rZ + minZ;
                        }
                        arr = f32;
                    } else if (a._quantized === 'u8color_delta') {
                        // Undo delta + Uint8 → Float32
                        const delta = new Uint8Array(buffers[a._bin].length);
                        delta.set(buffers[a._bin]);
                        const itemSize = a.itemSize;
                        for (let i = itemSize; i < delta.length; i++) {
                            delta[i] = (delta[i] + delta[i - itemSize]) & 0xFF;
                        }
                        const f32 = new Float32Array(delta.length);
                        for (let i = 0; i < delta.length; i++) f32[i] = delta[i] / 255;
                        arr = f32;
                    } else if (a._quantized === 'u8color') {
                        // Eski format: delta yok
                        const u8 = buffers[a._bin];
                        const f32 = new Float32Array(u8.length);
                        for (let i = 0; i < u8.length; i++) f32[i] = u8[i] / 255;
                        arr = f32;
                    } else {
                        // Eski format: byte unshuffle + opsiyonel XYZ reinterleave
                        let raw = buffers[a._bin];
                        if (a._shuffle) raw = byteUnshuffle(raw, a._shuffle);
                        if (a._deinterleaved && a._vtxCount) {
                            const f32 = new Float32Array(new Uint8Array(raw).buffer);
                            arr = reinterleaveXYZ(f32, a._vtxCount);
                        } else {
                            arr = FikoCizimYalin.bufferToTypedArray(raw, a.type);
                        }
                    }
                } else if (a.base64) {
                    arr = FikoCizimYalin.base64ToTypedArray(a.base64, a.type);
                }
                if (arr) geom.setAttribute(name, new THREE.BufferAttribute(arr, a.itemSize, false));
            }
        }
        if (data.index) {
            let arr: any;
            if (data.index._bin !== undefined && buffers[data.index._bin]) {
                let raw = buffers[data.index._bin];
                if (data.index._shuffle) raw = byteUnshuffle(raw, data.index._shuffle);
                arr = FikoCizimYalin.bufferToTypedArray(raw, data.index.type);
            } else if (data.index.base64) {
                arr = FikoCizimYalin.base64ToTypedArray(data.index.base64, data.index.type);
            }
            if (arr) geom.setIndex(new THREE.BufferAttribute(arr as any, 1));
        }
        // Normal'lar kayıtta atlandıysa yeniden hesapla
        if (data._normalsOmitted && geom.getAttribute('position')) {
            geom.computeVertexNormals();
        }
        return geom;
    }

    /** Raw Uint8Array → TypedArray (alignment güvenli) */
    private static bufferToTypedArray(bytes: Uint8Array, type: string): Float32Array | Uint32Array | Uint16Array | Int32Array | Uint8Array {
        const aligned = new Uint8Array(bytes.length);
        aligned.set(bytes);
        switch (type) {
            case 'Uint8Array': return aligned;
            case 'Uint32Array': return new Uint32Array(aligned.buffer);
            case 'Uint16Array': return new Uint16Array(aligned.buffer);
            case 'Int32Array': return new Int32Array(aligned.buffer);
            default: return new Float32Array(aligned.buffer);
        }
    }

    /** TypedArray → base64 string */
    private static typedArrayToBase64(arr: Float32Array | Uint32Array | Uint16Array | Int32Array): string {
        const bytes = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
        const CHUNK = 32768;
        let binary = '';
        for (let i = 0; i < bytes.length; i += CHUNK) {
            binary += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + CHUNK, bytes.length)) as any);
        }
        return btoa(binary);
    }

    /** base64 string → TypedArray */
    private static base64ToTypedArray(b64: string, type: string): Float32Array | Uint32Array | Uint16Array | Int32Array {
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        switch (type) {
            case 'Uint32Array': return new Uint32Array(bytes.buffer);
            case 'Uint16Array': return new Uint16Array(bytes.buffer);
            case 'Int32Array': return new Int32Array(bytes.buffer);
            default: return new Float32Array(bytes.buffer);
        }
    }

    static fromJson(sahne?: FikoSahne, parent?: any, json?: any, binBuffers?: Uint8Array[]) {
        let meshGeom: THREE.BufferGeometry, edgesGeom: THREE.BufferGeometry, vertexGeom: THREE.BufferGeometry;

        if (json.format === 'binary' && binBuffers) {
            // Binary container format (en verimli)
            meshGeom = FikoCizimYalin.binaryToGeometry(json.yuzeyler, binBuffers);
            edgesGeom = FikoCizimYalin.binaryToGeometry(json.sinirlar, binBuffers);
            vertexGeom = FikoCizimYalin.binaryToGeometry(json.noktalar, binBuffers);
        } else if (json.format === 'base64') {
            // Base64 compact format
            meshGeom = FikoCizimYalin.compactToGeometry(json.yuzeyler);
            edgesGeom = FikoCizimYalin.compactToGeometry(json.sinirlar);
            vertexGeom = FikoCizimYalin.compactToGeometry(json.noktalar);
        } else {
            // Eski format (geriye uyumluluk)
            meshGeom = sahne.anaGosterim.bufferGeometryLoader.parse(json.yuzeyler);
            edgesGeom = sahne.anaGosterim.bufferGeometryLoader.parse(json.sinirlar);
            vertexGeom = sahne.anaGosterim.bufferGeometryLoader.parse(json.noktalar);
        }

        const _rslt = new FikoCizimYalin(sahne, json.isim, meshGeom, edgesGeom, vertexGeom);
        _rslt.uuid = json.uuid;
        _rslt.parent = parent;

        if (json.userData) _rslt._restoreUserData(json.userData);
        if (json.visible !== undefined) _rslt.visible = json.visible;
        if (json.matrix) {
            _rslt.matrix.fromArray(json.matrix);
            _rslt.matrix.decompose(_rslt.position, _rslt.quaternion, _rslt.scale);
        }
        if (json.konumlar) {
            _rslt.konumlar = jsondan(sahne, _rslt, json.konumlar) as FikoKonumlar;
        }
        return _rslt;
    }

    toStr() {
        const yuzeyler = this.meshGeom.toJSON();
        const sinirlar = this.edgesGeom.toJSON();
        const noktalar = this.vertexGeom.toJSON();

        delete yuzeyler.userData;
        delete sinirlar.userData;
        delete noktalar.userData;

        let _rslt = `{ "uuid":"${this.uuid}","ismi": "${this.name}","type":"${this.type}",`;
        _rslt += `"yuzeyler":${JSON.stringify(yuzeyler)},`;
        _rslt += `"sinirlar":${JSON.stringify(sinirlar)},`;
        _rslt += `"noktalar":${JSON.stringify(noktalar)},`;
        _rslt += `"matrix":${JSON.stringify(this.matrix.toArray())}`;
        _rslt += `}`;

        return _rslt;
    }


    async export() {
        //@ts-ignore
        const newHandle = await window.showSaveFilePicker(
            {
                suggestedName: 'FikoÇizim',
                types: [{
                    description: 'FikoÇizim',
                    accept: {
                        'text/plain': ['.FikoCizim'],
                    },
                }],
            }
        );

        // create a FileSystemWritableFileStream to write to
        const writableStream = await newHandle.createWritable();

        let out = "";
        out += JSON.stringify(this.meshGeom.attributes['position'].toJSON()) + "|"
        out += JSON.stringify(this.meshGeom.attributes['color'].toJSON()) + "|"
        out += JSON.stringify(this.meshGeom.attributes['alpha'].toJSON()) + "|"
        out += JSON.stringify(this.meshGeom.attributes['grup'].toJSON())

        await writableStream.write(flate.gzip_encode(out));

        await writableStream.close();
    }

    async import() {
        //@ts-ignore
        const [fileHandle] = await window.showOpenFilePicker(
            {
                types: [{
                    description: 'Fiko Çizim Dosyaları',
                    accept: {
                        'text/plain': ['.FikoCizim'],
                    },
                }],
            }
        );

        //@ts-ignore
        const data = await fileHandle.getFile();

        var reader = new FileReader();
        reader.onload = function () {
            var text = reader.result;
            // Import result processing
        };
        reader.readAsText(data);

    }

    /**
     * Aktif pencere screenshot al ve clipboard'a kopyala
     */
    async fotografCek(): Promise<void> {
        let modalScaled = false;
        let uiModal: HTMLElement | null = null;
        let originalTransform = '';

        try {
            // Screen Capture API kullan - otomatik olarak aktif pencereyi seç
            if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
                throw new Error('Screen Capture API desteklenmiyor');
            }

            // 0. UI modal'ını bul ve 2x scale yap
            uiModal = this.findUIModal();
            if (uiModal) {
                originalTransform = uiModal.style.transform;
                // Önceki tüm transform etkilerini temizle
                uiModal.style.transform = '';
                // Sonra 2x scale yap
                uiModal.style.transform = 'scale(2)';
                uiModal.style.transformOrigin = 'center center';
                modalScaled = true;
                FikoThreeJsComponent.debugLog('UI modal 2x olarak ölçeklendirildi');
            }

            // 1. Screen Capture API kullan - basit ve uyumlu
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                } as MediaTrackConstraints,
                audio: false
            });

            // 2. Video oluştur
            const video = document.createElement('video');
            video.srcObject = stream;
            video.play();

            // 3. Frame almak için canvas oluştur
            return new Promise((resolve, reject) => {
                video.onloadedmetadata = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        canvas.width = video.videoWidth;
                        canvas.height = video.videoHeight;
                        const ctx = canvas.getContext('2d');

                        if (!ctx) {
                            stream.getTracks().forEach(track => track.stop());
                            reject(new Error('Canvas context oluşturulamadı'));
                            return;
                        }

                        // 4. 1 saniye sonra frame al (kullanıcının seçim yapması için zaman tanı)
                        setTimeout(() => {
                            // 5. Video frame'ini canvas'a çiz
                            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                            // 6. Stream'i durdur
                            stream.getTracks().forEach(track => track.stop());
                            video.remove();

                            // 7. Debug için küçük önizleme
                            const previewCanvas = document.createElement('canvas');
                            previewCanvas.width = canvas.width;
                            previewCanvas.height = canvas.height;
                            const previewCtx = previewCanvas.getContext('2d');

                            if (previewCtx) {
                                previewCtx.drawImage(canvas, 0, 0);
                                previewCanvas.style.position = 'fixed';
                                previewCanvas.style.top = '10px';
                                previewCanvas.style.left = '10px';
                                previewCanvas.style.zIndex = '99999';
                                previewCanvas.style.border = '2px solid red';
                                previewCanvas.style.maxWidth = '200px';
                                previewCanvas.style.maxHeight = '200px';
                                document.body.appendChild(previewCanvas);

                                // 3 saniye sonra kaldır
                                setTimeout(() => {
                                    if (previewCanvas.parentNode) {
                                        previewCanvas.parentNode.removeChild(previewCanvas);
                                    }
                                }, 3000);
                            }

                            // 8. Data URL al
                            const dataURL = canvas.toDataURL('image/png');

                            // 9. Modal'ı eski haline getir
                            if (modalScaled && uiModal) {
                                uiModal.style.transform = '';
                                uiModal.style.transform = originalTransform;
                                FikoThreeJsComponent.debugLog('UI modal orijinal boyutuna geri döndürüldü');
                                modalScaled = false;
                            }

                            // 10. Clipboard'a kopyala
                            this.kopyalaClipboard(dataURL)
                                .then(() => {
                                    this.bildirimGoster('📸 Ekran görüntüsü clipboard\'a kopyalandı! (Kutuda kontrol et)', 'success');
                                    FikoThreeJsComponent.debugLog('Ekran görüntüsü başarıyla clipboard\'a kopyalandı');
                                    resolve();
                                })
                                .catch((error) => {
                                    this.fallbackIndirme(dataURL, error);
                                    resolve();
                                });

                        }, 1000); // 1 saniye bekle

                    } catch (error) {
                        // Hata durumunda modal'ı eski haline getir
                        if (modalScaled && uiModal) {
                            uiModal.style.transform = '';
                            uiModal.style.transform = originalTransform;
                            FikoThreeJsComponent.debugLog('Hata durumunda UI modal eski haline getirildi');
                        }
                        stream.getTracks().forEach(track => track.stop());
                        video.remove();
                        reject(error);
                    }
                };

                video.onerror = () => {
                    // Hata durumunda modal'ı eski haline getir
                    if (modalScaled && uiModal) {
                        uiModal.style.transform = '';
                        uiModal.style.transform = originalTransform;
                        FikoThreeJsComponent.debugLog('Video hatasında UI modal eski haline getirildi');
                    }
                    stream.getTracks().forEach(track => track.stop());
                    video.remove();
                    reject(new Error('Video başlatılamadı'));
                };

                // Stream'i kontrol et
                stream.getVideoTracks()[0]?.addEventListener('ended', () => {
                    // Stream bitince modal'ı eski haline getir
                    if (modalScaled && uiModal) {
                        uiModal.style.transform = '';
                        uiModal.style.transform = originalTransform;
                        FikoThreeJsComponent.debugLog('Stream bitince UI modal eski haline getirildi');
                    }
                    stream.getTracks().forEach(track => track.stop());
                    video.remove();
                });
            });

        } catch (error) {
            // Hata durumunda modal'ı eski haline getir
            if (modalScaled && uiModal) {
                uiModal.style.transform = '';
                uiModal.style.transform = originalTransform;
                FikoThreeJsComponent.debugLog('Genel hata durumunda UI modal eski haline getirildi');
            }

            // Fallback: Basit canvas yöntemi
            await this.fallbackCanvas();
        }
    }

    /**
     * Clipboard kopyalama yardımcı
     */
    private async kopyalaClipboard(dataURL: string): Promise<void> {
        const blob = await (await fetch(dataURL)).blob();
        await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
        ]);
    }

    /**
     * Fallback indirme yardımcı
     */
    private fallbackIndirme(dataURL: string, error: any): void {
        const link = document.createElement('a');
        link.download = `Fiko3D_Ekran_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.webp`;
        link.href = dataURL;
        link.click();

        this.bildirimGoster('⬇️ Ekran görüntüsü indirildi (Clipboard desteklenmiyor)', 'warning');
    }

    /**
     * Fallback canvas yöntemi
     */
    private async fallbackCanvas(): Promise<void> {
        try {
            if (!this.sahne?.anaGosterim?.sunucu) {
                throw new Error('WebGL renderer bulunamadı');
            }

            // Son çare: WebGL renderer'ı direkt kullan
            const renderer = this.sahne.anaGosterim.sunucu;

            // Tüm sahneyi render et
            if (this.sahne.css3dSahne && this.sahne.anaGosterim.aktifKamera) {
                renderer.setClearColor(0x1a1a1a, 1); // Gri arka plan
                renderer.render(this.sahne.css3dSahne, this.sahne.anaGosterim.aktifKamera);
            }

            // WebGL canvas'ından direkt al
            const canvas = renderer.domElement;
            const dataURL = canvas.toDataURL('image/png');

            // Clipboard'a kopyala
            try {
                await this.kopyalaClipboard(dataURL);
                this.bildirimGoster('📸 WebGL görüntüsü clipboard\'a kopyalandı!', 'success');
            } catch (error) {
                this.fallbackIndirme(dataURL, error);
            }

        } catch (error) {
            this.bildirimGoster('❌ Görüntü alınamadı', 'error');
        }
    }

    /**
     * html2canvas kütüphanesini dinamik olarak yükle
     */
    private async html2canvasYukle(): Promise<void> {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('html2canvas yüklenemedi'));
            document.head.appendChild(script);
        });
    }

    /**
     * Fotoğraf için uygun container bul
     */
    private fotoContainerBul(): HTMLElement | null {
        // Önce Three.js container'ını bulmaya çalış
        const selectors = [
            '.fiko-three-js-container',
            '.canvas-container',
            '.three-container',
            'fikoyt-three-js',
            'canvas'
        ];

        for (const selector of selectors) {
            const element = document.querySelector(selector) as HTMLElement;
            if (element && (element.offsetWidth > 0 || element.offsetHeight > 0)) {
                return element;
            }
        }

        // Son çare: Three.js canvas'ın parent elementini bul
        if (this.sahne?.anaGosterim?.sunucu?.domElement) {
            const canvas = this.sahne.anaGosterim.sunucu.domElement;
            return (canvas.parentElement as HTMLElement) || canvas;
        }

        // En son çare: body
        return document.body as HTMLElement;
    }

    /**
     * UI modal elementini bul
     */
    private findUIModal(): HTMLElement | null {
        // Önce yaygın modal selector'larını dene
        const modalSelectors = [
            '.modal',
            '.ui-modal',
            '.dialog',
            '.popup',
            '[role="dialog"]',
            '.mat-dialog-container',
            '.cdk-overlay-pane',
            '.ant-modal',
            '.el-dialog',
            '.ui-dialog'
        ];

        for (const selector of modalSelectors) {
            const modal = document.querySelector(selector) as HTMLElement;
            if (modal && modal.style.display !== 'none') {
                return modal;
            }
        }

        // tinyMCE editor modal'ını kontrol et
        const tinymceModals = document.querySelectorAll('.tox-dialog');
        for (let i = 0; i < tinymceModals.length; i++) {
            const modal = tinymceModals[i] as HTMLElement;
            if (modal.style.display !== 'none') {
                return modal;
            }
        }

        // Son çare: en üstteki fixed veya absolute positioned elementi bul
        const allElements = document.querySelectorAll('*') as NodeListOf<HTMLElement>;
        let topElement: HTMLElement | null = null;
        let highestZ = -1;

        for (let i = 0; i < allElements.length; i++) {
            const element = allElements[i] as HTMLElement;
            const style = window.getComputedStyle(element);
            const zIndex = parseInt(style.zIndex) || 0;
            const position = style.position;

            if ((position === 'fixed' || position === 'absolute') &&
                zIndex > highestZ &&
                element.offsetWidth > 0 &&
                element.offsetHeight > 0) {
                highestZ = zIndex;
                topElement = element;
            }
        }

        return topElement;
    }

    /**
     * Bildirim gösteren yardımcı metod
     */
    private bildirimGoster(mesaj: string, tip: 'success' | 'warning' | 'error' = 'success'): void {
        const renkler = {
            success: '#4CAF50',
            warning: '#FF9800',
            error: '#f44336'
        };

        const notification = document.createElement('div');
        notification.textContent = mesaj;
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${renkler[tip]};
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            z-index: 10000;
            font-family: Arial, sans-serif;
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
            font-size: 14px;
            max-width: 300px;
            word-wrap: break-word;
        `;
        document.body.appendChild(notification);

        // 4 saniye sonra kaldır
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 4000);
    }

    get cncSuresi(): string {
        const estimator = new CncEstimator('aluminium');

        const cncTime = estimator.estimateFromGeometry(this.meshGeom);

        // Saniye cinsinden toplam süre
        const totalSeconds = Math.round(cncTime) * 2;

        // Saat, dakika, saniye hesapla
        const saat = Math.floor(totalSeconds / 3600);
        const dakika = Math.floor((totalSeconds % 3600) / 60);
        const saniye = totalSeconds % 60;

        // Formatla: "X saat Y dakika Z saniye"
        const parcalar = [];

        if (saat > 0) {
            parcalar.push(`${saat} saat`);
        }

        if (dakika > 0) {
            parcalar.push(`${dakika} dakika`);
        }

        if (saniye > 0 || parcalar.length === 0) {
            parcalar.push(`${saniye} saniye`);
        }

        return parcalar.join(' ');
    }
}